import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { Sparkles, Zap, Star, Crown, Gift } from 'lucide-react';
import confetti from 'canvas-confetti';

// Rarity configuration with enhanced visuals
const rarityConfig = {
  common: {
    color: '#A0A0A0',
    glowColor: 'rgba(160, 160, 160, 0.6)',
    rayColor: 'rgba(160, 160, 160, 0.3)',
    bgGradient: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
    particles: 30,
    screenShake: false,
    soundText: 'NICE!',
    vibrationPattern: [50],
  },
  rare: {
    color: '#00CCFF',
    glowColor: 'rgba(0, 204, 255, 0.8)',
    rayColor: 'rgba(0, 204, 255, 0.4)',
    bgGradient: 'linear-gradient(135deg, #000814 0%, #001d3d 100%)',
    particles: 50,
    screenShake: false,
    soundText: 'SWEET!',
    vibrationPattern: [100, 50, 100],
  },
  epic: {
    color: '#FF00FF',
    glowColor: 'rgba(255, 0, 255, 0.9)',
    rayColor: 'rgba(180, 0, 255, 0.5)',
    bgGradient: 'linear-gradient(135deg, #0d001a 0%, #1a0033 100%)',
    particles: 80,
    screenShake: true,
    soundText: 'EPIC DROP!',
    vibrationPattern: [100, 30, 100, 30, 200],
  },
  legendary: {
    color: '#FFD700',
    glowColor: 'rgba(255, 215, 0, 1)',
    rayColor: 'rgba(255, 180, 0, 0.6)',
    bgGradient: 'linear-gradient(135deg, #1a1400 0%, #332600 100%)',
    particles: 150,
    screenShake: true,
    soundText: 'LEGENDARY!!!',
    vibrationPattern: [200, 100, 200, 100, 400],
  },
};

// Explosion Particle Component
const ExplosionParticle = ({ delay, config, type = 'spark' }) => {
  const angle = Math.random() * Math.PI * 2;
  const distance = 150 + Math.random() * 300;
  const targetX = Math.cos(angle) * distance;
  const targetY = Math.sin(angle) * distance;
  const size = type === 'spark' ? (Math.random() * 6 + 2) : (Math.random() * 12 + 8);
  
  return (
    <motion.div
      className={`absolute ${type === 'confetti' ? 'rounded-sm' : 'rounded-full'}`}
      style={{
        width: size,
        height: type === 'confetti' ? size * 2 : size,
        background: type === 'confetti' 
          ? ['#FFD700', '#FF00FF', '#00FFFF', '#FF6B6B', '#7CFF6B'][Math.floor(Math.random() * 5)]
          : config.color,
        boxShadow: type === 'spark' ? `0 0 ${size * 3}px ${config.glowColor}` : 'none',
        left: '50%',
        top: '50%',
        transformOrigin: 'center',
      }}
      initial={{ x: 0, y: 0, scale: 0, opacity: 0, rotate: 0 }}
      animate={{
        x: [0, targetX * 0.3, targetX],
        y: [0, targetY * 0.3 - 50, targetY + 100],
        scale: [0, 1.5, 0.3],
        opacity: [0, 1, 0],
        rotate: type === 'confetti' ? [0, 360 * (Math.random() > 0.5 ? 1 : -1)] : 0,
      }}
      transition={{
        duration: type === 'confetti' ? 2 : 1.2,
        delay: delay,
        ease: 'easeOut',
      }}
    />
  );
};

// God Ray Component - Rotating light beams
const GodRays = ({ color, intensity = 1 }) => {
  return (
    <motion.div 
      className="absolute inset-0 pointer-events-none overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: intensity }}
      transition={{ duration: 0.5 }}
    >
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute left-1/2 top-1/2 origin-bottom"
          style={{
            width: '4px',
            height: '150vh',
            background: `linear-gradient(to top, ${color} 0%, transparent 100%)`,
            transform: `translateX(-50%) translateY(-100%) rotate(${i * 30}deg)`,
            filter: 'blur(8px)',
          }}
          animate={{
            rotate: [i * 30, i * 30 + 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      ))}
    </motion.div>
  );
};

// Holographic 3D Card with mouse/gyro tilt
const HolographicCard = ({ card, config, isRevealed }) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isGyroEnabled, setIsGyroEnabled] = useState(false);
  
  // Mouse move handler for desktop
  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current || isGyroEnabled) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const rotateY = ((e.clientX - centerX) / (rect.width / 2)) * 25;
    const rotateX = -((e.clientY - centerY) / (rect.height / 2)) * 25;
    
    setTilt({ x: rotateX, y: rotateY });
  }, [isGyroEnabled]);
  
  const handleMouseLeave = useCallback(() => {
    if (!isGyroEnabled) {
      setTilt({ x: 0, y: 0 });
    }
  }, [isGyroEnabled]);
  
  // Gyroscope handler for mobile
  useEffect(() => {
    const handleOrientation = (e) => {
      if (!isGyroEnabled) return;
      
      const rotateX = Math.max(-25, Math.min(25, e.beta - 45));
      const rotateY = Math.max(-25, Math.min(25, e.gamma));
      
      setTilt({ x: rotateX, y: rotateY });
    };
    
    // Try to enable gyroscope
    if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
      // iOS 13+
      DeviceOrientationEvent.requestPermission()
        .then(response => {
          if (response === 'granted') {
            setIsGyroEnabled(true);
            window.addEventListener('deviceorientation', handleOrientation);
          }
        })
        .catch(() => {});
    } else if ('DeviceOrientationEvent' in window) {
      // Non-iOS devices
      setIsGyroEnabled(true);
      window.addEventListener('deviceorientation', handleOrientation);
    }
    
    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, [isGyroEnabled]);
  
  // Holographic gradient position based on tilt
  const holoX = 50 + tilt.y * 2;
  const holoY = 50 + tilt.x * 2;
  
  return (
    <motion.div
      ref={cardRef}
      className="relative cursor-pointer"
      style={{
        perspective: '1000px',
        transformStyle: 'preserve-3d',
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      initial={{ scale: 0, rotateY: 180 }}
      animate={{ 
        scale: isRevealed ? [1.3, 1] : 0,
        rotateY: isRevealed ? [180, 0] : 180,
      }}
      transition={{ 
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: 0.2,
      }}
    >
      <motion.div
        className="relative w-72 h-96 rounded-2xl overflow-hidden"
        style={{
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transformStyle: 'preserve-3d',
          boxShadow: `
            0 0 60px ${config.glowColor},
            0 20px 60px rgba(0,0,0,0.5),
            inset 0 0 30px ${config.glowColor}
          `,
          border: `4px solid ${config.color}`,
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
        {/* Card Image */}
        {card?.image_base64 ? (
          <img
            src={`data:image/png;base64,${card.image_base64}`}
            alt={card?.character_name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
            <Gift className="w-24 h-24 text-white/30" />
          </div>
        )}
        
        {/* Holographic Overlay - Rainbow shine effect */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              linear-gradient(
                ${135 + tilt.y}deg,
                rgba(255,0,0,0) 0%,
                rgba(255,0,0,0.1) ${holoX - 30}%,
                rgba(255,165,0,0.15) ${holoX - 20}%,
                rgba(255,255,0,0.15) ${holoX - 10}%,
                rgba(0,255,0,0.15) ${holoX}%,
                rgba(0,255,255,0.15) ${holoX + 10}%,
                rgba(0,0,255,0.15) ${holoX + 20}%,
                rgba(128,0,255,0.1) ${holoX + 30}%,
                rgba(128,0,255,0) 100%
              )
            `,
            mixBlendMode: 'overlay',
          }}
          animate={{
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        
        {/* Shine/Glare effect */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at ${holoX}% ${holoY}%, rgba(255,255,255,0.4) 0%, transparent 50%)`,
          }}
        />
        
        {/* Rarity Badge */}
        <div 
          className="absolute top-3 left-3 px-3 py-1 rounded-full font-anton text-sm uppercase tracking-wider"
          style={{ 
            background: `${config.color}30`,
            color: config.color,
            border: `2px solid ${config.color}`,
          }}
        >
          {card?.rarity || 'common'}
        </div>
        
        {/* Power Stars */}
        <div className="absolute top-3 right-3 flex gap-1">
          {[...Array(card?.power_rating || 1)].map((_, i) => (
            <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
          ))}
        </div>
        
        {/* Card Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4">
          <h3 className="font-anton text-2xl text-white">{card?.character_name || 'Mystery Card'}</h3>
          <p className="font-space text-sm text-white/60">{card?.title || 'Unknown'}</p>
        </div>
        
        {/* Legendary crown for legendary cards */}
        {card?.rarity === 'legendary' && (
          <motion.div
            className="absolute -top-8 left-1/2 transform -translate-x-1/2"
            animate={{ y: [0, -5, 0], rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Crown className="w-12 h-12 text-yellow-400 drop-shadow-[0_0_20px_rgba(255,215,0,0.8)]" />
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Main Pack Opening Animation Component
const GodTierPackOpening = ({ card, onComplete }) => {
  const [phase, setPhase] = useState('buildup'); // buildup, flash, reveal, celebrate
  const [showParticles, setShowParticles] = useState(false);
  
  const config = rarityConfig[card?.rarity] || rarityConfig.common;
  
  // Trigger haptic feedback
  const triggerHaptic = useCallback((pattern) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }, []);
  
  // Trigger confetti explosion
  const triggerConfetti = useCallback(() => {
    const duration = 3000;
    const end = Date.now() + duration;
    
    const colors = card?.rarity === 'legendary' 
      ? ['#FFD700', '#FFA500', '#FFFF00'] 
      : card?.rarity === 'epic'
        ? ['#FF00FF', '#8B00FF', '#FF69B4']
        : ['#00FFFF', '#00FF00', '#FF00FF'];
    
    const frame = () => {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 70,
        origin: { x: 0, y: 0.6 },
        colors,
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 70,
        origin: { x: 1, y: 0.6 },
        colors,
      });
      
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    
    frame();
  }, [card?.rarity]);
  
  // Animation timeline
  useEffect(() => {
    // Phase 1: Buildup - Pack shaking (2s)
    const flashTimer = setTimeout(() => {
      setPhase('flash');
      triggerHaptic([50, 30, 50, 30, 100]);
    }, 2000);
    
    // Phase 2: Flash - White screen flash (0.3s)
    const revealTimer = setTimeout(() => {
      setPhase('reveal');
      setShowParticles(true);
      triggerHaptic(config.vibrationPattern);
      
      if (card?.rarity === 'epic' || card?.rarity === 'legendary') {
        triggerConfetti();
      }
    }, 2300);
    
    // Phase 3: Celebrate (after 1s)
    const celebrateTimer = setTimeout(() => {
      setPhase('celebrate');
    }, 3500);
    
    return () => {
      clearTimeout(flashTimer);
      clearTimeout(revealTimer);
      clearTimeout(celebrateTimer);
    };
  }, [config.vibrationPattern, card?.rarity, triggerHaptic, triggerConfetti]);
  
  return (
    <motion.div
      className="fixed inset-0 z-[2000] flex items-center justify-center overflow-hidden cursor-pointer"
      onClick={() => phase === 'celebrate' && onComplete && onComplete()}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Dark Background Overlay */}
      <motion.div 
        className="absolute inset-0 bg-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.95 }}
      />
      
      {/* Background Gradient */}
      <motion.div
        className="absolute inset-0"
        style={{ background: config.bgGradient }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />
      
      {/* God Rays - Only show after reveal */}
      <AnimatePresence>
        {(phase === 'reveal' || phase === 'celebrate') && (
          <GodRays color={config.rayColor} intensity={card?.rarity === 'legendary' ? 1 : 0.7} />
        )}
      </AnimatePresence>
      
      {/* Central Glow */}
      <motion.div
        className="absolute w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${config.glowColor} 0%, transparent 70%)`,
          filter: 'blur(40px)',
        }}
        animate={{
          scale: phase === 'flash' ? [1, 4, 2] : phase === 'reveal' ? [2, 1.5] : 1,
          opacity: phase === 'flash' ? [0.5, 1, 0.6] : 0.4,
        }}
        transition={{ duration: 0.5 }}
      />
      
      {/* Screen Flash */}
      <AnimatePresence>
        {phase === 'flash' && (
          <motion.div
            className="absolute inset-0 bg-white z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>
      
      {/* Explosion Particles */}
      <AnimatePresence>
        {showParticles && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Sparks */}
            {[...Array(config.particles)].map((_, i) => (
              <ExplosionParticle key={`spark-${i}`} delay={i * 0.01} config={config} type="spark" />
            ))}
            {/* Confetti pieces */}
            {(card?.rarity === 'epic' || card?.rarity === 'legendary') && 
              [...Array(40)].map((_, i) => (
                <ExplosionParticle key={`confetti-${i}`} delay={i * 0.02} config={config} type="confetti" />
              ))
            }
          </div>
        )}
      </AnimatePresence>
      
      {/* Main Content */}
      <div className="relative z-10">
        <AnimatePresence mode="wait">
          {/* BUILDUP PHASE - Shaking Pack */}
          {phase === 'buildup' && (
            <motion.div
              key="buildup"
              className="relative"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ 
                scale: 1.5, 
                opacity: 1,
                rotate: [0, -8, 8, -6, 6, -4, 4, -2, 2, 0],
                x: [0, -15, 15, -10, 10, -8, 8, -5, 5, 0],
              }}
              exit={{ scale: 2, opacity: 0 }}
              transition={{
                scale: { duration: 0.5 },
                rotate: { duration: 0.4, repeat: Infinity },
                x: { duration: 0.3, repeat: Infinity },
              }}
            >
              {/* Glowing Pack Box */}
              <motion.div 
                className="w-56 h-72 rounded-2xl flex flex-col items-center justify-center relative overflow-hidden"
                style={{
                  background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
                  border: `4px solid ${config.color}`,
                  boxShadow: `
                    0 0 30px ${config.glowColor},
                    0 0 60px ${config.glowColor},
                    inset 0 0 20px rgba(255,255,255,0.1)
                  `,
                }}
                animate={{
                  boxShadow: [
                    `0 0 30px ${config.glowColor}, 0 0 60px ${config.glowColor}`,
                    `0 0 60px ${config.glowColor}, 0 0 100px ${config.glowColor}`,
                    `0 0 30px ${config.glowColor}, 0 0 60px ${config.glowColor}`,
                  ],
                }}
                transition={{ duration: 0.5, repeat: Infinity }}
              >
                {/* Inner glow effect */}
                <motion.div
                  className="absolute inset-0"
                  style={{
                    background: `radial-gradient(circle at 50% 30%, ${config.glowColor} 0%, transparent 60%)`,
                  }}
                  animate={{ opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                />
                
                {/* Sparkle icon */}
                <motion.div
                  animate={{ 
                    y: [0, -15, 0],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{ duration: 0.4, repeat: Infinity }}
                >
                  <Sparkles className="w-20 h-20" style={{ color: config.color }} />
                </motion.div>
                
                {/* OPENING text */}
                <motion.p 
                  className="mt-6 font-anton text-2xl uppercase tracking-widest"
                  style={{ color: config.color }}
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                >
                  OPENING...
                </motion.p>
                
                {/* Energy particles inside box */}
                <div className="absolute inset-0 overflow-hidden">
                  {[...Array(10)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-2 h-2 rounded-full"
                      style={{ 
                        background: config.color,
                        left: `${20 + Math.random() * 60}%`,
                        boxShadow: `0 0 10px ${config.glowColor}`,
                      }}
                      animate={{
                        y: ['100%', '-100%'],
                        opacity: [0, 1, 0],
                      }}
                      transition={{
                        duration: 1,
                        delay: i * 0.1,
                        repeat: Infinity,
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
          
          {/* REVEAL PHASE - Card Slam */}
          {(phase === 'reveal' || phase === 'celebrate') && (
            <motion.div
              key="reveal"
              className="flex flex-col items-center"
              initial={{ y: -200 }}
              animate={{ y: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              {/* Sound Text */}
              <motion.div
                className="absolute -top-20"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: [0, 1.5, 1], opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <p 
                  className="font-anton text-5xl uppercase"
                  style={{ 
                    color: config.color,
                    textShadow: `
                      0 0 20px ${config.glowColor},
                      0 0 40px ${config.glowColor},
                      0 0 60px ${config.glowColor}
                    `,
                  }}
                >
                  {config.soundText}
                </p>
              </motion.div>
              
              {/* The Holographic Card */}
              <HolographicCard card={card} config={config} isRevealed={true} />
              
              {/* Click to continue */}
              {phase === 'celebrate' && (
                <motion.p
                  className="mt-8 font-space text-white/60"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  Click anywhere to continue...
                </motion.p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Screen Shake for Epic/Legendary */}
      {config.screenShake && phase === 'reveal' && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            x: [0, -15, 15, -10, 10, -5, 5, 0],
            y: [0, 10, -10, 5, -5, 0],
          }}
          transition={{ duration: 0.4 }}
        />
      )}
    </motion.div>
  );
};

export default GodTierPackOpening;
